import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EXECUTOR_DEPT_GRID } from '../constants/executorDepartmentLayout';
import { Department, Employee } from '../services/api';
import './TaskExecutorsPanel.css';

export interface ExecutorsDraft {
  department_ids: number[];
  assignee_employee_ids: number[];
}

export interface PeerExecutorInDept {
  employeeId: number;
  fullName: string;
  taskPk: number;
  workType: string;
}

interface TaskExecutorsPanelProps {
  departments: Department[];
  employeesByDepartment: Map<number, Employee[]>;
  value: ExecutorsDraft;
  onChange: (next: ExecutorsDraft) => void;
  disabled?: boolean;
  peerAssigneesByDepartment?: Map<number, PeerExecutorInDept[]>;
}

const TaskExecutorsPanel: React.FC<TaskExecutorsPanelProps> = ({
  departments,
  employeesByDepartment,
  value,
  onChange,
  disabled,
  peerAssigneesByDepartment,
}) => {
  const [openPickerDeptId, setOpenPickerDeptId] = useState<number | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  const departmentById = useMemo(() => {
    const map = new Map<number, Department>();
    for (const dept of departments) {
      map.set(dept.id, dept);
    }
    return map;
  }, [departments]);

  const assigneeIdSet = useMemo(() => new Set(value.assignee_employee_ids), [value.assignee_employee_ids]);

  const rebuildDepartmentIds = (assignees: Set<number>): number[] => {
    const nextDeptIds = new Set<number>();
    for (const dept of departments) {
      const deptEmployees = employeesByDepartment.get(dept.id) ?? [];
      if (deptEmployees.some((emp) => assignees.has(emp.id))) {
        nextDeptIds.add(dept.id);
      }
    }
    return [...nextDeptIds];
  };

  const toggleAssignee = (employeeId: number): void => {
    const selected = assigneeIdSet.has(employeeId);
    const nextAssignees = new Set(value.assignee_employee_ids);

    if (selected) {
      nextAssignees.delete(employeeId);
    } else {
      nextAssignees.add(employeeId);
    }

    onChange({
      department_ids: rebuildDepartmentIds(nextAssignees),
      assignee_employee_ids: [...nextAssignees],
    });
  };

  const renderDepartmentBlock = (dept: Department): React.ReactNode => {
    const deptEmployees = employeesByDepartment.get(dept.id) ?? [];
    const assignedInDept = deptEmployees.filter((emp) => assigneeIdSet.has(emp.id));
    const peersInDept = (peerAssigneesByDepartment?.get(dept.id) ?? []).filter(
      (peer) => !assigneeIdSet.has(peer.employeeId),
    );
    const isPickerOpen = openPickerDeptId === dept.id;
    const query = pickerSearch.trim().toLowerCase();
    const filteredForPicker = deptEmployees.filter((emp) => {
      if (!query) return true;
      return emp.full_name.toLowerCase().includes(query);
    });

    return (
      <div key={dept.id} className="task-executors__block">
        <div className="task-executors__block-head">
          <span className="task-executors__dept-label">
            <span className="task-executors__dept-name">{dept.name}</span>
          </span>
          <button
            type="button"
            className="task-executors__add-btn"
            aria-label={`Добавить исполнителя в отдел ${dept.name}`}
            disabled={disabled}
            onClick={() => {
              if (isPickerOpen) {
                setOpenPickerDeptId(null);
                setPickerSearch('');
              } else {
                setOpenPickerDeptId(dept.id);
                setPickerSearch('');
              }
            }}
          >
            +
          </button>
        </div>
        <div className="task-executors__list" role="list">
          {assignedInDept.length === 0 && peersInDept.length === 0 ? (
            <div className="task-executors__list-empty">Нет назначенных сотрудников</div>
          ) : (
            <>
              {assignedInDept.map((emp) => (
                <div key={`${dept.id}-${emp.id}`} className="task-executors__person" role="listitem">
                  <span>{emp.full_name}</span>
                </div>
              ))}
              {peersInDept.map((peer) => (
                <div
                  key={`${dept.id}-peer-${peer.employeeId}-${peer.taskPk}`}
                  className="task-executors__person task-executors__person--peer"
                  role="listitem"
                >
                  <span>{peer.fullName}</span>
                  <Link
                    to={`/tasks/${peer.taskPk}`}
                    className="task-executors__peer-task-link"
                    title={`Задача: ${peer.workType}`}
                  >
                    {peer.workType}
                  </Link>
                </div>
              ))}
            </>
          )}
        </div>
        {isPickerOpen ? (
          <div className="task-executors__picker">
            <input
              type="search"
              className="task-executors__picker-search"
              placeholder="Поиск сотрудника"
              value={pickerSearch}
              disabled={disabled}
              onChange={(e) => setPickerSearch(e.target.value)}
            />
            <div className="task-executors__picker-list">
              {filteredForPicker.length === 0 ? (
                <div className="task-executors__list-empty">
                  {query ? 'Ничего не найдено' : 'Нет сотрудников с доступом к отделу'}
                </div>
              ) : (
                filteredForPicker.map((emp) => (
                  <label key={`picker-${dept.id}-${emp.id}`} className="task-executors__person" role="listitem">
                    <input
                      type="checkbox"
                      checked={assigneeIdSet.has(emp.id)}
                      disabled={disabled}
                      onChange={() => toggleAssignee(emp.id)}
                    />
                    <span>{emp.full_name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  if (departments.length === 0) {
    return <p className="task-executors__empty">Нет отделов-исполнителей.</p>;
  }

  const layoutCells = EXECUTOR_DEPT_GRID.flatMap((row, rowIndex) =>
    row.map((departmentId, colIndex) => ({ departmentId, key: `${rowIndex}-${colIndex}` })),
  );

  return (
    <div className="task-executors">
      {layoutCells.map((cell) => {
        if (cell.departmentId == null) {
          return <div key={cell.key} className="task-executors__block task-executors__block--placeholder" aria-hidden />;
        }
        const dept = departmentById.get(cell.departmentId);
        if (!dept) {
          return <div key={cell.key} className="task-executors__block task-executors__block--placeholder" aria-hidden />;
        }
        return renderDepartmentBlock(dept);
      })}
    </div>
  );
};

export default TaskExecutorsPanel;
